// Clientul de burse din browser: lumânări REST (cu cache) + stream-uri WS multiplexate.
//
// O singură conexiune per (bursă, piață), indiferent câte grafice sunt deschise.
// Abonamentele sunt numărate pe referințe: ultimul consumator care pleacă dezabonează topicul.
// La reconectare se retrimit toate abonamentele active.

import { adapter, nativeSymbol } from './exchanges.js';
import { bus } from './util.js';

const RECONNECT_BASE = 1000;
const RECONNECT_MAX = 20000;
const PING_INTERVAL = 15000;
/**
 * Cât așteptăm date după ce ne-am abonat, până să declarăm stream-ul mut.
 *
 * Unele rețele lasă handshake-ul WebSocket să treacă și chiar confirmă abonarea, dar
 * nu livrează niciun mesaj — se întâmplă cu `fstream.binance.com` (futures) în
 * jurisdicțiile unde derivatele sunt blocate, în timp ce spotul merge normal.
 * Un socket „deschis" nu înseamnă date, deci verificăm livrarea, nu conexiunea.
 */
const SILENCE_MS = 12000;
const WATCHDOG_MS = 4000;
/** Cât ascultăm fluxul-martor (tranzacții BTC) înainte de a da verdictul. */
const PROBE_MS = 7000;
/**
 * Cadența cu care cerem prin REST ultima lumânare, în paralel cu WebSocket-ul.
 *
 * Măsurat pe scalpx.ru (45 s, un grafic deschis): pe lângă `kline.1.SYMBOL` — care
 * livrează la ~2000 ms — ei cer `v5/market/kline?limit=1` la fiecare ~997 ms, mereu,
 * nu doar când socketul tace. De acolo vine senzația că graficul lor e viu permanent.
 */
const HEARTBEAT_MS = 1000;
/** Tabelul de tickere: la ei ~7998 ms între interogări. */
const TICKER_MS = 8000;
/**
 * Bătaia se rărește când sunt multe grafice deschise (grila), ca să nu depășim
 * atâtea cereri pe secundă către bursă.
 *
 * Măsurat pe grilă: la 12 cereri/s Binance răspunde cu 429, apoi cu 418 — interzicere
 * temporară de adresă. Bugetul nu e doar al paginii: serverul nostru cere de pe același
 * IP (agregate, interes deschis, sparkline-uri), așa că partea browserului rămâne mică.
 */
const MAX_KLINE_RPS = 3;
/** Cât stăm deoparte după ce bursa ne-a refuzat pentru ritm prea mare. */
const BACKOFF_MS = 60_000;

/** Un socket pentru o pereche (bursă, piață) — sau pentru o rută a ei, unde bursa le separă. */
class Connection {
  constructor(exchange, market, route = '') {
    this.exchange = exchange;
    this.market = market;
    this.route = route;
    this.adapter = adapter(exchange);
    this.ws = null;
    this.ready = false;
    this.attempt = 0;
    this.msgId = 1;
    this.pingTimer = null;
    this.reconnectTimer = null;
    this.closed = false;
    /** topic -> Set<callback> */
    this.subs = new Map();
    /** topic-uri de trimis când socketul devine gata */
    this.pending = new Set();
    this.silent = false;
    this.lastMessageAt = 0;
    this.watchdog = setInterval(() => this.#checkSilence(), WATCHDOG_MS);
  }

  /**
   * Un stream care nu livrează nimic e la fel de rău ca unul închis.
   *
   * Dar liniștea singură nu dovedește nimic: o monedă fără tranzacții tace legitim.
   * De aceea, când bănuim o problemă, ne abonăm la un flux despre care știm că e mereu
   * activ (tranzacțiile BTC). Dacă nici acela nu vine, fluxul chiar e blocat.
   */
  #checkSilence() {
    if (this.closed || !this.subs.size || !this.ready || this.probing) return;
    const since = Date.now() - (this.lastMessageAt || this.subscribedAt || Date.now());
    if (since <= SILENCE_MS) return;
    this.#probe();
  }

  #probe() {
    const native = nativeSymbol(this.exchange, this.market, 'BTCUSDT');
    // Pe socketul de stakan nu vin tranzacții, deci martorul e stakanul BTC.
    const kind = this.route === 'depth' ? 'depth' : 'trade';
    const topic = this.adapter.topic(kind, native, '1m', this.market);
    if (!topic) { this.#declare(true); return; }

    this.probing = true;
    let alive = false;
    const off = this.subscribe(topic, () => { alive = true; });
    setTimeout(() => {
      off();
      this.probing = false;
      this.#declare(!alive);
      if (alive) this.lastMessageAt = Date.now();
    }, PROBE_MS);
  }

  #declare(silent) {
    if (silent === this.silent) return;
    this.silent = silent;
    // Tăcerea socketului de stakan nu spune nimic despre lumânări și tickere: nu
    // trecem toată perechea pe interogare din cauza lui.
    if (this.route) return;
    bus.emit(silent ? 'ws:silent' : 'ws:alive', {
      exchange: this.exchange, market: this.market,
    });
  }

  connect() {
    if (this.closed || this.ws) return;
    const url = this.adapter.wsUrl(this.market, this.route);
    let ws;
    try {
      ws = new WebSocket(url);
    } catch (err) {
      console.warn(`[ws:${this.exchange}] ${err}`);
      this.scheduleReconnect();
      return;
    }
    this.ws = ws;

    ws.onopen = () => {
      this.ready = true;
      this.attempt = 0;
      bus.emit('ws:open', { exchange: this.exchange, market: this.market });
      // La reconectare, retrimitem tot ce e activ.
      this.pending = new Set(this.subs.keys());
      this.flush();
      this.startPing();
    };

    ws.onmessage = (event) => this.handleMessage(event.data);

    ws.onerror = () => { /* onclose se ocupă de reconectare */ };

    ws.onclose = () => {
      this.ready = false;
      this.stopPing();
      this.ws = null;
      bus.emit('ws:close', { exchange: this.exchange, market: this.market });
      if (!this.closed) this.scheduleReconnect();
    };
  }

  scheduleReconnect() {
    if (this.closed || this.reconnectTimer) return;
    const delay = Math.min(RECONNECT_MAX, RECONNECT_BASE * 2 ** this.attempt++);
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay);
  }

  startPing() {
    this.stopPing();
    this.pingTimer = setInterval(() => {
      if (!this.ready) return;
      const key = this.exchange;
      try {
        if (key === 'bybit') this.ws.send(JSON.stringify({ op: 'ping' }));
        else if (key === 'okx' || key === 'bitget') this.ws.send('ping');
        else if (key === 'gate') {
          this.ws.send(JSON.stringify({
            time: Math.floor(Date.now() / 1000),
            channel: this.market === 'futures' ? 'futures.ping' : 'spot.ping',
          }));
        } else if (key === 'mexc') {
          this.ws.send(JSON.stringify(
            this.market === 'futures' ? { method: 'ping' } : { method: 'PING' },
          ));
        }
        // Binance răspunde singur la ping-urile de protocol.
      } catch { /* socketul se închide singur */ }
    }, PING_INTERVAL);
  }

  stopPing() {
    if (this.pingTimer) clearInterval(this.pingTimer);
    this.pingTimer = null;
  }

  handleMessage(data) {
    if (typeof data !== 'string') return;
    if (data === 'pong' || data === 'ping') return;
    let raw;
    try { raw = JSON.parse(data); } catch { return; }

    // Confirmarea abonării nu e dată de piață: nu resetează ceasul de tăcere.
    const isAck = raw.result === null || raw.event === 'subscribe' || raw.op === 'subscribe';
    if (!isAck) {
      this.lastMessageAt = Date.now();
      if (this.silent) {
        this.silent = false;
        if (!this.route) bus.emit('ws:alive', { exchange: this.exchange, market: this.market });
      }
    }

    // Confirmări de abonare / pong-uri — le ignorăm.
    if (raw.op === 'pong' || raw.pong || raw.event === 'subscribe'
        || raw.event === 'unsubscribe' || raw.result === null) return;

    let parsed;
    try { parsed = this.adapter.parse(raw); } catch { return; }
    if (!parsed) return;

    const handlers = this.subs.get(parsed.topic);
    if (!handlers) return;
    for (const fn of handlers) {
      try { fn(parsed.payload, parsed.kind); } catch (err) { console.error('[ws handler]', err); }
    }
  }

  flush() {
    if (!this.ready || !this.pending.size) return;
    const topics = [...this.pending];
    this.pending.clear();
    // De aici pornește numărătoarea până la declararea tăcerii.
    this.subscribedAt = Date.now();
    if (this.adapter.oneAtATime) {
      for (const t of topics) this.send(this.adapter.subMessage([t], this.msgId++, this.market));
    } else {
      this.send(this.adapter.subMessage(topics, this.msgId++, this.market));
    }
  }

  send(message) {
    // `ready` poate fi stale dacă socketul tocmai s-a închis — verificăm și starea reală.
    if (!this.ready || this.ws?.readyState !== WebSocket.OPEN) return;
    try { this.ws.send(JSON.stringify(message)); } catch { /* ignorat */ }
  }

  subscribe(topic, fn) {
    let handlers = this.subs.get(topic);
    if (!handlers) {
      handlers = new Set();
      this.subs.set(topic, handlers);
      this.pending.add(topic);
      if (this.ready) this.flush();
      else this.connect();
    }
    handlers.add(fn);

    return () => {
      const set = this.subs.get(topic);
      if (!set) return;
      set.delete(fn);
      if (set.size) return;
      this.subs.delete(topic);
      this.pending.delete(topic);
      if (this.ready) this.send(this.adapter.unsubMessage([topic], this.msgId++, this.market));
    };
  }

  close() {
    this.closed = true;
    this.stopPing();
    clearInterval(this.watchdog);
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.subs.clear();
    try { this.ws?.close(); } catch { /* ignorat */ }
    this.ws = null;
  }
}

class ExchangeClient {
  #connections = new Map();       // "binance:futures" -> Connection
  #klineCache = new Map();        // cheie -> { bars, at }
  #inflight = new Map();          // cheie -> Promise
  #cacheTtl = 15_000;
  /** Abonamente de lumânări, servite și prin REST în paralel cu stream-ul. */
  #klineSubs = new Map();         // "binance:futures" -> Map("SYMBOL|tf" -> {fns, lastWs, busy})
  #tickerSubs = new Map();        // "binance:futures" -> Set<fn>
  #beats = new Map();             // "binance:futures" -> intervalId (lumânări, ~1 s)
  #tickerTimers = new Map();      // "binance:futures" -> intervalId (tickere, ~8 s)
  #backoff = new Map();           // "binance:futures" -> până când stăm deoparte
  #silent = new Set();            // perechile al căror stream nu livrează nimic

  constructor() {
    bus.on('ws:silent', ({ exchange, market }) => {
      const key = `${exchange}:${market}`;
      if (this.#silent.has(key)) return;
      this.#silent.add(key);
      console.warn(`[ws] ${key} nu livrează date — rămâne doar interogarea periodică`);
      bus.emit('feed:degraded', { exchange, market });
    });
    bus.on('ws:alive', ({ exchange, market }) => {
      const key = `${exchange}:${market}`;
      if (!this.#silent.delete(key)) return;
      bus.emit('feed:live', { exchange, market });
    });
  }

  #connection(exchange, market, kind = '') {
    const route = adapter(exchange).wsRoute?.(kind, market) || '';
    const key = route ? `${exchange}:${market}:${route}` : `${exchange}:${market}`;
    let conn = this.#connections.get(key);
    if (!conn) {
      conn = new Connection(exchange, market, route);
      this.#connections.set(key, conn);
      conn.connect();
    }
    return conn;
  }

  // --------------------------------------------------- bătaia de o secundă

  /**
   * Ritmul graficelor. Merge în paralel cu stream-ul, nu în locul lui: dacă WebSocket-ul
   * a livrat ceva pentru simbolul ăsta de curând, sărim peste cerere — bara e deja
   * proaspătă. Dacă tace, o secundă mai târziu tot vine o valoare nouă prin REST, așa
   * că graficul se mișcă indiferent ce face rețeaua.
   */
  #startBeat(key) {
    if (this.#beats.has(key)) return;
    const [exchange, market] = key.split(':');
    const tick = () => this.#beat(exchange, market, key);
    this.#beats.set(key, setInterval(tick, HEARTBEAT_MS));
  }

  #beat(exchange, market, key) {
    const perPair = this.#klineSubs.get(key);
    if (!perPair?.size) { this.#stopBeat(key); return; }
    // Bursa ne-a refuzat de curând: insistând, interzicerea se prelungește.
    if (Date.now() < (this.#backoff.get(key) || 0)) return;

    // Bugetul de cereri se împarte: graficele privite (panoul mare) primesc ritmul
    // întreg, restul — cardurile din grilă — își împart ce rămâne. Fără asta, un
    // grafic deschis peste o grilă de zeci de carduri ar fi la fel de lent ca ele.
    let prioritare = 0;
    for (const e of perPair.values()) if (e.priority) prioritare++;
    const restul = perPair.size - prioritare;
    // Cel puțin o cerere pe secundă rămâne pentru carduri, oricâte prioritare ar fi.
    const bugetRest = Math.max(1, MAX_KLINE_RPS - prioritare);
    const gapRest = restul ? Math.max(HEARTBEAT_MS, Math.round((restul * 1000) / bugetRest)) : HEARTBEAT_MS;

    const now = Date.now();
    for (const [subKey, entry] of perPair) {
      // `setInterval` livrează tick-ul și la 999 ms, nu doar la 1000: fără marjă,
      // exact acel tick ar fi respins, iar cadența reală s-ar dubla la două secunde.
      const due = (entry.priority ? HEARTBEAT_MS : gapRest) - 50;
      if (entry.busy) continue;                    // cererea trecută n-a răspuns încă
      if (now - entry.lastWs < due) continue;      // stream-ul ține deja ritmul
      if (now - entry.lastPull < due) continue;
      entry.lastPull = now;
      entry.busy = true;
      this.#pullBar(exchange, market, subKey, entry)
        .finally(() => { entry.busy = false; });
    }
  }

  async #pullBar(exchange, market, subKey, entry) {
    const [symbol, tf] = subKey.split('|');
    try {
      const native = nativeSymbol(exchange, market, symbol);
      // Două bare, nu una ca la ScalpX: exact la schimbarea de lumânare, `limit=1`
      // lasă bara închisă cu valoarea de acum o secundă. A doua bară costă la fel
      // și îi dă valoarea finală.
      const bars = await adapter(exchange).restKlines(market, native, tf, 2);
      if (!bars.length) return;
      const last = bars[bars.length - 1];
      for (const bar of bars) {
        for (const fn of entry.fns) {
          // Un abonat care aruncă nu trebuie să-i blocheze pe ceilalți.
          try { fn({ bar, closed: bar !== last }, 'kline'); }
          catch (err) { console.warn(`[beat] ${subKey}: ${err?.message || err}`); }
        }
      }
    } catch (err) {
      const msg = String(err?.message || err);
      // 429 = ritm prea mare, 418 = adresă interzisă fiindcă am insistat după 429.
      // Ambele cer pauză: altfel pierdem și istoricul, nu doar bătaia.
      if (/\b(418|429)\b/.test(msg)) {
        const key = `${exchange}:${market}`;
        if (Date.now() >= (this.#backoff.get(key) || 0)) {
          console.warn(`[beat] ${key}: bursa limitează ritmul — pauză ${BACKOFF_MS / 1000}s`);
        }
        this.#backoff.set(key, Date.now() + BACKOFF_MS);
        return;
      }
      // Un eșec tăcut aici înseamnă grafice înghețate fără niciun indiciu.
      if (!this.beatWarned) {
        this.beatWarned = true;
        console.warn(`[beat] ${exchange}:${market} ${subKey}: ${msg}`);
      }
    }
  }

  #stopBeat(key) {
    const id = this.#beats.get(key);
    if (!id) return;
    clearInterval(id);
    this.#beats.delete(key);
  }

  // ------------------------------------------------ tabelul de tickere (~8 s)

  #startTickerPoll(exchange, market) {
    const key = `${exchange}:${market}`;
    if (this.#tickerTimers.has(key)) return;

    const tick = async () => {
      const subs = this.#tickerSubs.get(key);
      if (!subs?.size) { this.#stopTickerPoll(key); return; }
      try {
        const resp = await fetch(`/api/aggregates?exchange=${exchange}&market=${market}`);
        const data = await resp.json();
        const rows = Object.entries(data.data || {}).map(([symbol, row]) => ({ symbol, ...row }));
        for (const fn of subs) fn(rows, 'tickerAll');
      } catch { /* reîncercăm la următoarea bătaie */ }
    };

    tick();
    this.#tickerTimers.set(key, setInterval(tick, TICKER_MS));
  }

  #stopTickerPoll(key) {
    const id = this.#tickerTimers.get(key);
    if (!id) return;
    clearInterval(id);
    this.#tickerTimers.delete(key);
  }

  /** Fluxul acestei perechi vine prin WebSocket sau doar prin interogare? */
  degraded(exchange, market) {
    return this.#silent.has(`${exchange}:${market}`);
  }

  /** Bursa ne-a limitat ritmul și stăm deoparte? */
  throttled(exchange, market) {
    return Date.now() < (this.#backoff.get(`${exchange}:${market}`) || 0);
  }

  // ------------------------------------------------------------ lumânări

  /**
   * Istoricul de lumânări. Deduplică request-urile concurente pentru aceeași cheie
   * și memorează rezultatul câteva secunde — grila cere aceleași date de mai multe ori.
   */
  async klines(exchange, market, symbol, tf, limit = 500, endTime = null) {
    const key = `${exchange}:${market}:${symbol}:${tf}:${limit}:${endTime || 'now'}`;
    const cached = this.#klineCache.get(key);
    // Istoricul mai vechi nu se schimbă niciodată — îl ținem cât ține sesiunea.
    const ttl = endTime ? Infinity : this.#cacheTtl;
    if (cached && Date.now() - cached.at < ttl) return cached.bars;

    const running = this.#inflight.get(key);
    if (running) return running;

    const native = nativeSymbol(exchange, market, symbol);
    const promise = adapter(exchange)
      .restKlines(market, native, tf, limit, endTime)
      .then((bars) => {
        this.#klineCache.set(key, { bars, at: Date.now() });
        return bars;
      })
      .catch(async (err) => {
        // Limitare de ritm: fallback-ul nu ajută. În dezvoltare serverul iese pe aceeași
        // adresă, deci cererea e sigur irosită; în producție am muta limitarea pe serverul
        // comun tuturor utilizatorilor. Ne oprim și lăsăm bursa să se liniștească.
        if (/\b(418|429)\b/.test(String(err?.message || err))) {
          const pair = `${exchange}:${market}`;
          if (Date.now() >= (this.#backoff.get(pair) || 0)) {
            console.warn(`[klines] ${pair}: bursa limitează ritmul — pauză ${BACKOFF_MS / 1000}s`);
          }
          this.#backoff.set(pair, Date.now() + BACKOFF_MS);
          throw err;
        }
        // Bursa e blocată sau a picat: încercăm fallback-ul de pe serverul nostru.
        console.warn(`[klines] ${key}: ${err.message} — trec pe fallback`);
        const resp = await fetch(
          `/api/klines?exchange=${exchange}&market=${market}`
          + `&symbol=${encodeURIComponent(symbol)}&tf=${tf}&limit=${limit}`
          + (endTime ? `&end=${endTime}` : ''),
        );
        if (!resp.ok) throw err;
        const data = await resp.json();
        const bars = data.bars.map((b) => ({
          time: Math.floor(b[0] / 1000),
          open: b[1], high: b[2], low: b[3], close: b[4], volume: b[5],
        }));
        this.#klineCache.set(key, { bars, at: Date.now() });
        return bars;
      })
      .finally(() => this.#inflight.delete(key));

    this.#inflight.set(key, promise);
    return promise;
  }

  // ------------------------------------------------------------- stream-uri

  /**
   * @param kind 'kline' | 'trade' | 'depth' | 'ticker'
   * @returns funcție de dezabonare
   */
  subscribe(exchange, market, symbol, kind, fn, tf = '1m', opts = {}) {
    const a = adapter(exchange);
    const native = nativeSymbol(exchange, market, symbol);
    const topic = a.topic(kind, native, tf, market);
    if (!topic) return () => {};

    // Lumânările sunt singurele pe care le putem servi și prin REST.
    if (kind !== 'kline') return this.#connection(exchange, market, kind).subscribe(topic, fn);

    const key = `${exchange}:${market}`;
    const subKey = `${symbol}|${tf}`;
    if (!this.#klineSubs.has(key)) this.#klineSubs.set(key, new Map());
    const perPair = this.#klineSubs.get(key);
    if (!perPair.has(subKey)) {
      perPair.set(subKey, { fns: new Set(), lastWs: 0, lastPull: 0, busy: false, priority: false });
    }
    const entry = perPair.get(subKey);
    entry.fns.add(fn);
    if (opts.priority) entry.priority = true;

    // Marcăm momentul fiecărui mesaj de stream: bătaia de o secundă îl folosește
    // ca să nu ceară prin REST ceva ce tocmai a sosit prin socket.
    const seen = (msg, k) => { entry.lastWs = Date.now(); fn(msg, k); };
    const off = this.#connection(exchange, market).subscribe(topic, seen);
    this.#startBeat(key);

    return () => {
      off();
      entry.fns.delete(fn);
      if (!entry.fns.size) perPair.delete(subKey);
      if (!perPair.size) this.#stopBeat(key);
    };
  }

  /**
   * Actualizări pentru toate simbolurile. Bursele fără stream „toate tickerele"
   * (Bybit, OKX…) merg direct pe interogare; restul trec pe ea doar dacă tac.
   */
  subscribeTickerAll(exchange, market, fn) {
    const key = `${exchange}:${market}`;
    if (!this.#tickerSubs.has(key)) this.#tickerSubs.set(key, new Set());
    this.#tickerSubs.get(key).add(fn);

    const topic = adapter(exchange).topic('tickerAll', '', '1m', market);
    let off = () => {};
    // Bursele care au stream „toate tickerele" îl folosesc; celelalte cad pe interogare,
    // la cadența măsurată la ei. Snapshot-ul din `state` merge oricum în paralel.
    if (topic) off = this.#connection(exchange, market).subscribe(topic, fn);
    else this.#startTickerPoll(exchange, market);

    return () => {
      off();
      const subs = this.#tickerSubs.get(key);
      subs?.delete(fn);
      if (!subs?.size) this.#stopTickerPoll(key);
    };
  }

  /** Închide toate conexiunile — folosit la schimbarea bursei active. */
  disconnectAll() {
    for (const conn of this.#connections.values()) conn.close();
    this.#connections.clear();
    for (const id of this.#beats.values()) clearInterval(id);
    this.#beats.clear();
    for (const id of this.#tickerTimers.values()) clearInterval(id);
    this.#tickerTimers.clear();
    this.#klineSubs.clear();
    this.#tickerSubs.clear();
    this.#backoff.clear();
    this.#silent.clear();
  }

  /** Starea internă a abonamentelor — pentru diagnosticarea fluxului de date. */
  debugState() {
    const now = Date.now();
    const klines = {};
    for (const [key, perPair] of this.#klineSubs) {
      klines[key] = [...perPair].map(([subKey, e]) =>
        `${subKey}×${e.fns.size} ws:${e.lastWs ? `${now - e.lastWs}ms` : '—'}`);
    }
    return {
      klines,
      tickere: [...this.#tickerSubs].map(([k, s]) => `${k}×${s.size}`),
      batai: [...this.#beats.keys()],
      mute: [...this.#silent],
    };
  }

  status() {
    return [...this.#connections.entries()].map(([key, c]) => ({
      key, ready: c.ready, topics: c.subs.size,
    }));
  }
}

export const client = new ExchangeClient();
